import { useState } from "react";
import type { GalleryPreset, ApplyMode } from "@profolio/templates";
import { resolvePresetComposition } from "@profolio/templates";
import {
  Sparkles,
  Palette,
  Layout,
  CheckCircle2,
  Plus,
  X,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface TemplateApplyModalProps {
  preset: GalleryPreset | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirmApply: (options: {
    presetId: string;
    mode: ApplyMode;
    sectionsToAdd: string[];
  }) => void;
  existingSectionTypes?: string[];
  userTier?: "starter" | "pro" | "premium";
}

export function TemplateApplyModal({
  preset,
  isOpen,
  onClose,
  onConfirmApply,
  existingSectionTypes = [],
  userTier = "starter",
}: TemplateApplyModalProps) {
  const [selectedMode, setSelectedMode] = useState<ApplyMode>("visual-only");
  const [sectionsToAdd, setSectionsToAdd] = useState<string[]>([]);

  if (!isOpen || !preset) return null;

  const composition = resolvePresetComposition(preset.presetId);
  const blueprint = composition?.blueprint;
  const tierAdaptation =
    blueprint?.adaptations[userTier] || blueprint?.adaptations.starter;

  // Identify sections featured in this blueprint that the user does not currently have
  const blueprintSections = new Set<string>();
  tierAdaptation?.pages.forEach((p) => {
    p.defaultSectionOrder.forEach((sec) => blueprintSections.add(sec));
  });

  const missingSections = Array.from(blueprintSections).filter(
    (sec) => !existingSectionTypes.includes(sec)
  );

  const toggleSectionToAdd = (type: string) => {
    setSectionsToAdd((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleApply = () => {
    onConfirmApply({
      presetId: preset.presetId,
      mode: selectedMode,
      sectionsToAdd: selectedMode === "design-and-organize" ? sectionsToAdd : [],
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-xs animate-in fade-in-50 duration-200">
      <div className="relative w-full max-w-lg rounded-2xl border border-border bg-card shadow-2xl overflow-hidden p-6 select-none">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
        >
          <X className="h-4 w-4" />
        </button>

        {/* ── Modal Header ── */}
        <div className="flex items-start gap-3.5 mb-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0 border border-primary/20">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-foreground">
              Apply "{preset.name}" Template
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
              Choose how you want to apply this design to your academic website draft.
            </p>
          </div>
        </div>

        {/* Starter Plan Notice for Pro/Premium Preset */}
        {preset.requiredTier !== "starter" && userTier === "starter" && (
          <div className="flex items-center gap-2.5 p-3 rounded-xl bg-amber-500/10 text-xs text-amber-800 dark:text-amber-300 mb-4 border border-amber-500/20">
            <Sparkles className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span>
              This is a <strong>{preset.requiredTier.toUpperCase()}</strong> template. Applying it on the Starter plan adapts it to free-tier compatible styling to ensure your draft saves smoothly.
            </span>
          </div>
        )}

        {/* ── Mode Selection Cards ── */}
        <div className="space-y-3 mb-5">
          {/* Option 1: Visual Design Only */}
          <div
            onClick={() => setSelectedMode("visual-only")}
            className={`p-4 rounded-xl border transition-all cursor-pointer flex items-start gap-3.5 ${
              selectedMode === "visual-only"
                ? "border-primary bg-primary/5 shadow-xs"
                : "border-border hover:border-border/80 bg-background/50"
            }`}
          >
            <div className="mt-0.5 text-primary">
              <Palette className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-foreground">
                  Apply Visual Design Only
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  RECOMMENDED
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-normal">
                Updates theme layout, color palette, typography pairing, and hero style.
                Your existing publications, bio, and custom sections remain 100% untouched.
              </p>
            </div>
          </div>

          {/* Option 2: Design + Organize Sections */}
          <div
            onClick={() => setSelectedMode("design-and-organize")}
            className={`p-4 rounded-xl border transition-all cursor-pointer flex items-start gap-3.5 ${
              selectedMode === "design-and-organize"
                ? "border-primary bg-primary/5 shadow-xs"
                : "border-border hover:border-border/80 bg-background/50"
            }`}
          >
            <div className="mt-0.5 text-primary">
              <Layout className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <span className="text-xs font-bold text-foreground">
                Apply Design + Organize Sections
              </span>
              <p className="text-[11px] text-muted-foreground mt-1 leading-normal">
                Updates visual styles and arranges your existing sections according to
                the template's recommended layout order. Never deletes any content.
              </p>

              {/* Optional Checklist for Missing Sections */}
              {selectedMode === "design-and-organize" && missingSections.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border/60">
                  <span className="text-[11px] font-semibold text-foreground block mb-2">
                    Template sections you don't currently have (optional to add):
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {missingSections.map((secType) => {
                      const isChecked = sectionsToAdd.includes(secType);
                      return (
                        <button
                          key={secType}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSectionToAdd(secType);
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-all cursor-pointer ${
                            isChecked
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-muted text-muted-foreground border-border hover:text-foreground"
                          }`}
                        >
                          {isChecked ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <Plus className="h-3 w-3" />
                          )}
                          <span className="capitalize">{secType}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Safety & Undo Banner ── */}
        <div className="flex items-center gap-2 p-3 rounded-xl bg-muted/60 text-[11px] text-muted-foreground mb-6 border border-border/40">
          <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span>
            <strong>Draft-only & Reversible:</strong> Applying this template modifies
            your working draft. Your live published site will not change until you click "Publish".
          </span>
        </div>

        {/* ── Action Buttons ── */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-xs font-medium cursor-pointer"
          >
            Cancel
          </Button>

          <Button
            type="button"
            size="sm"
            onClick={handleApply}
            className="text-xs font-bold cursor-pointer"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Apply to Draft
          </Button>
        </div>
      </div>
    </div>
  );
}
